import { getInstanceId } from '@va/dashboard/selectors/app';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { ListIcon, ResetIcon, TrashCanIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainRecord,
  CustomDomainsApi,
  CustomDomainTarget,
  CustomDomainType,
} from '@va/shared/api/custom-domains';
import { useListWebsiteDomainsByType } from '@va/shared/api/custom-domains/custom-domains.api.hooks';
import { CustomDomainAction, ViewDNSRecordsModal } from '@va/shared/custom-domains';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { getTrackingCode } from '@va/standalone/shared/actions';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteActiveWebsiteTrackingCodeDomainConfirmationModal } from './confirmation-modals/DeleteActiveWebsiteTrackingCodeDomainConfirmationModal';
import { PendingWebsiteTrackingCodeDomainConfirmationModal } from './confirmation-modals/PendingWebsiteTrackingCodeDomainConfirmationModal';
import { CustomTrackingCodeDomainCreationModal } from './creation/CustomTrackingCodeDomainCreationModal';

export const useWebsiteTrackingCodeDomainsActions = () => {
  const websiteId = useSelector(getInstanceId);

  const translate = useTranslate();
  const dispatch = useDispatch();

  const { mutate } = useListWebsiteDomainsByType({ websiteId: websiteId, type: CustomDomainType.trackingCode });

  const { showErrorNotification } = useAddNotification();

  const { confirm } = useConfirmationDialogContext();

  const cancelPendingCustomDomain = useCallback(
    async (customDomainId: number) => {
      try {
        await CustomDomainsApi.cancelPendingWebsiteDomain({ websiteId: websiteId, customDomainId: customDomainId });
        await mutate();
      } catch (error) {
        console.error(error);
        showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
      }
    },
    [mutate, showErrorNotification, translate, websiteId],
  );

  const deleteCustomDomain = useCallback(
    async (customDomainId: number) => {
      try {
        await CustomDomainsApi.deleteWebsiteDomain({ websiteId: websiteId, customDomainId: customDomainId });
        await mutate();
        dispatch(getTrackingCode());
      } catch (error) {
        console.error(error);
        showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
      }
    },
    [dispatch, mutate, showErrorNotification, translate, websiteId],
  );

  const openViewDNSRecordsModal = useCallback(
    (domain: string) => {
      confirm(
        () => {},
        ({ onClose, onConfirm }) => (
          <ViewDNSRecordsModal
            platform={CustomDomainPlatformType.STANDALONE}
            isOpen={true}
            title={translate('customDomain.labels.dashboardDNSRecordsTitle')}
            expectedDNSRecordsCount={2}
            onConfirm={onConfirm}
            onClose={onClose}
            domain={domain}
            target={CustomDomainTarget.trackingCode}
            onFailure={(err) => {
              showErrorNotification(undefined, err);
            }}
          />
        ),
      );
    },
    [confirm, showErrorNotification, translate],
  );

  const activeCustomDomainActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(customDomain.domain);
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteCustomDomain(customDomain.id);
            }, DeleteActiveWebsiteTrackingCodeDomainConfirmationModal);
          },
        },
      ];
    },
    [confirm, deleteCustomDomain, openViewDNSRecordsModal, translate],
  );

  const pendingCustomDomainActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(customDomain.domain);
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.cancel'),
          onClick: () => {
            confirm(() => {
              cancelPendingCustomDomain(customDomain.id);
            }, PendingWebsiteTrackingCodeDomainConfirmationModal);
          },
        },
      ];
    },
    [cancelPendingCustomDomain, confirm, openViewDNSRecordsModal, translate],
  );

  const inactiveCustomDomainsActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ResetIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.retryActivation'),
          onClick: () => {
            confirm(
              () => {},
              ({ onClose }) => (
                <CustomTrackingCodeDomainCreationModal
                  domain={customDomain.domain}
                  isOpen={true}
                  closeModal={onClose}
                  customDomainId={customDomain.id}
                />
              ),
            );
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            deleteCustomDomain(customDomain.id);
          },
        },
      ];
    },
    [confirm, deleteCustomDomain, translate],
  );

  return {
    activeCustomDomainActions,
    pendingCustomDomainActions,
    inactiveCustomDomainsActions,
  };
};
